import React, { ReactElement, useMemo } from 'react';
import { getResidentialUnitsByProjectId } from '@/services/residential-units';
import { groupBy } from 'lodash-es';
import { LoadingIndicator, StickyContainer } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { useAsyncState } from '@/utils/hooks';
import { getResidentialUnitGroupsByProjectId } from '@/services/residential-unit-groups';
import { reduce } from 'lodash-es';
import { ResidentialUnit, ResidentialUnitGroup } from '@/types';
import { ButtonPagination, useLocalPagination } from '@/components/ScrollPagination';

interface ResidentialUnitGroupingProps {
    items: any[];
    projectId: number;
    selector?: (item) => number;
    emptyText?: ReactElement;
    paginate?: boolean;
    children: (
        data: any[],
        residentialUnit: ResidentialUnit,
        residentialUnitGroup: ResidentialUnitGroup | null
    ) => ReactElement;
}

const ResidentialUnitGrouping: React.FC<ResidentialUnitGroupingProps> = ({
    projectId,
    children,
    items,
    selector = (item) => item.residentialUnitId,
    paginate,
    emptyText = (
        <span className="text-missing">
            <Trans>No Results</Trans>
        </span>
    ),
}) => {
    const [residentialUnits] = useAsyncState(() => getResidentialUnitsByProjectId(projectId), [projectId], null);
    const [residentialUnitGroups] = useAsyncState(() => getResidentialUnitGroupsByProjectId(projectId), [projectId], null);

    const data = useMemo(() => {
        if (!residentialUnits || !residentialUnitGroups) {
            return null;
        }

        const groupedItems = groupBy(items, selector);
        const residentialUnitGroupsMap = reduce(
            residentialUnitGroups,
            (result, value) => {
                result[value.id] = value;
                return result;
            },
            {} as { [key: number]: ResidentialUnitGroup }
        );

        return residentialUnits
            .map((ru) => ({
                items: groupedItems[ru.id] || [],
                residentialUnit: ru,
                residentialUnitGroup:
                    (ru.residentialUnitGroupId && residentialUnitGroupsMap[ru.residentialUnitGroupId]) || null,
            }))
            .filter((x) => x.items.length > 0);
    }, [residentialUnits, residentialUnitGroups, items, selector]);

    const pagination = useLocalPagination(data || [], paginate ? 10 : null);

    if (!data) {
        return <LoadingIndicator />;
    }

    // const residentialUnitsWithData = residentialUnits.filter((ru) => groupedItems[ru.id]);

    if (data.length === 0) {
        return emptyText;
    }

    return (
        <StickyContainer>
            <div style={{ position: 'relative' }}>
                <ButtonPagination loader={pagination}>
                    {(pageData) => (
                        <>
                            {pageData.map((x) => (
                                <div key={x.residentialUnit.id}>
                                    {children(x.items, x.residentialUnit, x.residentialUnitGroup)}
                                </div>
                            ))}
                        </>
                    )}
                </ButtonPagination>
            </div>
        </StickyContainer>
    );
};

export default ResidentialUnitGrouping;
